import React, { createContext, useCallback, useEffect, useRef, useState, useContext } from 'react';
import { useAuth } from './AuthContext';
import { buildConsoleRequestHeaders } from '../utils/backendRequest';

const OutgoingWebhooksContext = createContext();

function outgoingRequestHeaders(token, user) {
  return buildConsoleRequestHeaders(token, user);
}

export function OutgoingWebhooksProvider({ children }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [error, setError] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [deliveriesError, setDeliveriesError] = useState(null);
  const refreshInFlightRef = useRef(false);
  const deliveriesInFlightRef = useRef(false);
  const subscriptionsRef = useRef(subscriptions);
  const deliveriesRef = useRef(deliveries);

  useEffect(() => {
    subscriptionsRef.current = subscriptions;
  }, [subscriptions]);

  useEffect(() => {
    deliveriesRef.current = deliveries;
  }, [deliveries]);

  const refreshSubscriptions = useCallback(async () => {
    if (refreshInFlightRef.current) {
      return subscriptionsRef.current;
    }

    refreshInFlightRef.current = true;
    const token = localStorage.getItem('token');
    if (!token) {
      setSubscriptions([]);
      setError('Missing access token');
      refreshInFlightRef.current = false;
      return [];
    }

    try {
      setLoading(true);
      setError(null);
      const webhooksApiUrl = process.env.REACT_APP_WEBHOOKS_API_URL || '';
      const subscriptionsUrl = `${webhooksApiUrl}/v1/webhooks/subscriptions`;
      const subscriptionsResponse = await fetch(subscriptionsUrl, {
        headers: outgoingRequestHeaders(token, user),
      });
  
      if (subscriptionsResponse.ok) {
        const data = await subscriptionsResponse.json();
        const list = Array.isArray(data) ? data : [];
        setSubscriptions(list);
        return list;
      }

      setSubscriptions([]);
      setError(`Failed to load subscriptions (${subscriptionsResponse.status})`);
      return [];
    } catch (error) {
      console.error('Failed to pull webhook subscriptions:', error);
      setSubscriptions([]);
      setError('Failed to pull webhook subscriptions');
      return [];
    } finally {
      setLoading(false);
      refreshInFlightRef.current = false;
    }
  }, [user]);

  const updateSubscription = useCallback(async (id, patch) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Missing access token');
      return { ok: false };
    }

    const webhooksApiUrl = process.env.REACT_APP_WEBHOOKS_API_URL || '';
    const baseUrl = `${webhooksApiUrl}/v1/webhooks/subscriptions/${encodeURIComponent(id)}`;

    const tryRequest = async (method) => {
      return fetch(baseUrl, {
        method,
        headers: {
          ...outgoingRequestHeaders(token, user),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(patch || {}),
      });
    };

    try {
      setSaving(true);
      setError(null);

      let res = await tryRequest('PUT');
      if (res.ok) {
        await refreshSubscriptions();
        return { ok: true };
      }

      setError(`Failed to update subscription (${res.status})`);
      return { ok: false, status: res.status };
    } catch (error) {
      console.error('Failed to update webhook subscription:', error);
      setError('Failed to update webhook subscription');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  }, [refreshSubscriptions, user]);

  const createSubscription = useCallback(async (payload) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Missing access token');
      return { ok: false };
    }

    try {
      setSaving(true);
      setError(null);
      const webhooksApiUrl = process.env.REACT_APP_WEBHOOKS_API_URL || '';
      const subscriptionsUrl = `${webhooksApiUrl}/v1/webhooks/subscriptions`;
      const res = await fetch(subscriptionsUrl, {
        method: 'POST',
        headers: {
          ...outgoingRequestHeaders(token, user),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload || {}),
      });

      if (res.ok) {
        await refreshSubscriptions();
        return { ok: true };
      }

      setError(`Failed to create subscription (${res.status})`);
      return { ok: false, status: res.status };
    } catch (error) {
      console.error('Failed to create webhook subscription:', error);
      setError('Failed to create webhook subscription');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  }, [refreshSubscriptions, user]);

  const deleteSubscription = useCallback(async (id) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Missing access token');
      return { ok: false };
    }

    try {
      setSaving(true);
      setError(null);
      const webhooksApiUrl = process.env.REACT_APP_WEBHOOKS_API_URL || '';
      const url = `${webhooksApiUrl}/v1/webhooks/subscriptions/${encodeURIComponent(id)}`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: outgoingRequestHeaders(token, user),
      });

      if (res.ok) {
        await refreshSubscriptions();
        return { ok: true };
      }

      setError(`Failed to delete subscription (${res.status})`);
      return { ok: false, status: res.status };
    } catch (error) {
      console.error('Failed to delete webhook subscription:', error);
      setError('Failed to delete webhook subscription');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  }, [refreshSubscriptions, user]);

  const refreshOutgoingWebhooks = useCallback(async ({ from, to } = {}) => {
    if (deliveriesInFlightRef.current) {
      return deliveriesRef.current;
    }

    deliveriesInFlightRef.current = true;
    const token = localStorage.getItem('token');
    if (!token) {
      setDeliveries([]);
      setDeliveriesError('Missing access token');
      deliveriesInFlightRef.current = false;
      return [];
    }

    try {
      setDeliveriesLoading(true);
      setDeliveriesError(null);
      const webhooksApiUrl = process.env.REACT_APP_WEBHOOKS_API_URL || '';
      const url = new URL(`${webhooksApiUrl}/v1/webhooks/outgoing`, window.location.origin);
      if (from) url.searchParams.set('from_timestamp', from);
      if (to) url.searchParams.set('to_timestamp', to);

      const res = await fetch(url.toString(), {
        headers: outgoingRequestHeaders(token, user),
      });

      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setDeliveries(list);
        return list;
      }

      setDeliveries([]);
      setDeliveriesError(`Failed to load outgoing webhooks (${res.status})`);
      return [];
    } catch (err) {
      console.error('Failed to pull outgoing webhooks:', err);
      setDeliveries([]);
      setDeliveriesError('Failed to pull outgoing webhooks');
      return [];
    } finally {
      setDeliveriesLoading(false);
      deliveriesInFlightRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    refreshSubscriptions();
  }, [refreshSubscriptions]);

  return (
    <OutgoingWebhooksContext.Provider value={{
      subscriptions,
      refreshSubscriptions,
      createSubscription,
      updateSubscription,
      deleteSubscription,
      loading,
      saving,
      error,
      deliveries,
      refreshOutgoingWebhooks,
      deliveriesLoading,
      deliveriesError,
    }}>
      {children}
    </OutgoingWebhooksContext.Provider>
  );
}

export function useWebhooks() {
  return useContext(OutgoingWebhooksContext);
}